import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sun, Moon } from "lucide-react";
import { publicApi, type AttemptState } from "@client/api/public";
import { COUNTRIES } from "@/lib/countries";
import { EXAM_RULES } from "@/lib/exam-rules";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";

export const Route = createFileRoute("/s/$token")({
  head: () => ({
    meta: [
      { title: "Referee Examination Entry | African Karate Federation" },
      {
        name: "description",
        content:
          "Enter your referee details to join the in-hall karate examination session.",
      },
      { property: "og:title", content: "Referee Examination Entry" },
      {
        property: "og:description",
        content: "Join the in-hall karate referee examination session.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RefereePage,
});

function storageKey(token: string) {
  return `karate-referee-attempt:${token}`;
}

function RefereePage() {
  const { token } = Route.useParams();
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("referee-theme") === "dark";
    }
    return false;
  });

  const toggleDark = () => {
    setIsDark((prev) => {
      const next = !prev;
      window.localStorage.setItem("referee-theme", next ? "dark" : "light");
      return next;
    });
  };

  const [participantToken, setParticipantToken] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<{
    title: string;
    examName: string;
    status: string;
  } | null>(null);
  const [state, setState] = useState<AttemptState | null>(null);

  useEffect(() => {
    if (state?.phase === "active" && isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [isDark, state?.phase]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    examinationNumber: "",
    country: "",
    language: "",
    antiCheatingConsent: false,
  });
  const [remaining, setRemaining] = useState(0);
  const skewRef = useRef(0);

  useEffect(() => {
    const stored = window.localStorage.getItem(storageKey(token));
    if (stored) setParticipantToken(stored);
    publicApi
      .session(token)
      .then((s) =>
        setSessionInfo({
          title: s.title,
          examName: s.examName,
          status: s.status,
        }),
      )
      .catch((e: Error) => setError(e.message));
  }, [token]);

  const refresh = useCallback(async () => {
    if (!participantToken) return;
    try {
      const next = await publicApi.attempt(participantToken);
      skewRef.current = next.serverTime - Date.now();
      setState(next);
      if (next.phase === "active" && next.remainingMs != null)
        setRemaining(next.remainingMs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection problem");
    }
  }, [participantToken]);

  // Poll the server for the authoritative timeline.
  useEffect(() => {
    if (!participantToken) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), 3000);
    return () => window.clearInterval(id);
  }, [participantToken, refresh]);

  // Local countdown between polls.
  useEffect(() => {
    if (state?.phase !== "active") return;
    const id = window.setInterval(
      () => setRemaining((r) => Math.max(0, r - 200)),
      200,
    );
    return () => window.clearInterval(id);
  }, [state?.phase, state?.questionIndex]);

  // Heartbeat + focus & screenshot monitoring.
  useEffect(() => {
    if (!participantToken || state?.phase !== "active") return;
    const beat = window.setInterval(
      () => void publicApi.heartbeat(participantToken),
      15000,
    );

    const onVisibility = () => {
      if (document.hidden) {
        void publicApi.event(participantToken, { type: "TAB_SWITCH" });
        void publicApi.event(participantToken, { type: "PAGE_HIDDEN" });
      } else {
        void publicApi.event(participantToken, { type: "PAGE_VISIBLE" });
      }
    };
    const onBlur = () => {
      void publicApi.event(participantToken, { type: "WINDOW_BLUR" });
    };

    const preventDefault = (e: Event) => e.preventDefault();

    const onKeyUp = (e: KeyboardEvent) => {
      if (
        e.key === "PrintScreen" ||
        e.code === "PrintScreen" ||
        e.keyCode === 44
      ) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          void navigator.clipboard.writeText("");
        }
        void publicApi.event(participantToken, { type: "SCREENSHOT_ATTEMPT" });
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("contextmenu", preventDefault);
    document.addEventListener("copy", preventDefault);
    document.addEventListener("cut", preventDefault);
    document.addEventListener("dragstart", preventDefault);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.clearInterval(beat);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("contextmenu", preventDefault);
      document.removeEventListener("copy", preventDefault);
      document.removeEventListener("cut", preventDefault);
      document.removeEventListener("dragstart", preventDefault);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [participantToken, state?.phase]);

  async function onJoin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        examinationNumber: form.examinationNumber,
        country: form.country,
        language: form.language,
        antiCheatingConsent: form.antiCheatingConsent,
      };
      const res = await publicApi.join(token, payload);
      window.localStorage.setItem(storageKey(token), res.participantToken);
      setParticipantToken(res.participantToken);
      toast.success("You are registered. Wait for the instructor to start.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not join the examination",
      );
    } finally {
      setLoading(false);
    }
  }

  async function answer(value: boolean) {
    if (!participantToken || state?.questionIndex == null) return;
    setState((s) => (s ? { ...s, selectedAnswer: value } : s));
    try {
      await publicApi.answer(participantToken, {
        questionIndex: state.questionIndex,
        answer: value,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Answer not saved");
      void refresh();
    }
  }

  if (error) {
    return (
      <Screen>
        <h1 className="hall-heading text-2xl font-bold">Entry not available</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      </Screen>
    );
  }

  if (!participantToken) {
    return (
      <Screen>
        <div className="flex flex-col items-center justify-center mb-6">
          <div className="flex h-48 w-48 sm:h-52 sm:w-52 items-center justify-center rounded-3xl bg-white p-5 shadow-2xl border border-border/20 ring-8 ring-black/5 dark:ring-white/10">
            <img
              src="/UFAK_Karate_Africa_Vertical.svg"
              alt="African Karate Federation"
              className="h-full w-full object-contain drop-shadow-sm"
            />
          </div>
        </div>
        <p className="hall-heading text-sm font-semibold text-foreground text-center">
          African Karate Federation
          <br />
          Referees Committee
        </p>
        <h1 className="hall-heading text-2xl font-bold">
          {sessionInfo?.title ?? "Examination entry"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {sessionInfo?.examName}
        </p>
        {sessionInfo && sessionInfo.status !== "WAITING" ? (
          <p className="mt-6 rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            This examination has already started. Late entry is not permitted.
          </p>
        ) : (
          <form onSubmit={onJoin} className="mt-6 space-y-4 text-left">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name (Same as Passport) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last / Family Name (Same as Passport) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="examinationNumber">
                Examination Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="examinationNumber"
                value={form.examinationNumber}
                onChange={(e) =>
                  setForm({ ...form, examinationNumber: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">
                Country <span className="text-destructive">*</span>
              </Label>
              <select
                id="country"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                required
              >
                <option value="" disabled className="bg-background text-foreground">
                  Select country
                </option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c} className="bg-background text-foreground">
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">
                Language <span className="text-destructive">*</span>
              </Label>
              <select
                id="language"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                required
              >
                <option value="" disabled className="bg-background text-foreground">
                  Select language
                </option>
                <option value="English" className="bg-background text-foreground">
                  English
                </option>
                <option value="Spanish" className="bg-background text-foreground">
                  Spanish
                </option>
                <option value="French" className="bg-background text-foreground">
                  French
                </option>
              </select>
            </div>

            {(() => {
              const currentRules =
                EXAM_RULES[form.language] || EXAM_RULES["English"]!;
              return (
                <>
                  <div className="rounded-md border border-border p-4 text-sm text-muted-foreground">
                    <h3 className="mb-2 font-bold text-foreground">
                      {currentRules.title}
                    </h3>
                    <ul className="list-inside list-disc space-y-1">
                      {currentRules.rules.map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-start gap-2 pt-2">
                    <input
                      id="antiCheatingConsent"
                      type="checkbox"
                      className="mt-1"
                      checked={form.antiCheatingConsent}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          antiCheatingConsent: e.target.checked,
                        })
                      }
                      required
                    />
                    <Label
                      htmlFor="antiCheatingConsent"
                      className="text-sm font-normal"
                    >
                      {currentRules.consentText} <span className="text-destructive">*</span>
                    </Label>
                  </div>
                </>
              );
            })()}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={
                loading ||
                !form.firstName.trim() ||
                !form.lastName.trim() ||
                !form.examinationNumber.trim() ||
                !COUNTRIES.includes(form.country as any) ||
                !form.language ||
                !form.antiCheatingConsent
              }
            >
              {loading ? "Registering…" : "Continue"}
            </Button>
          </form>
        )}
      </Screen>
    );
  }

  if (!state) {
    return (
      <Screen>
        <p className="text-sm text-muted-foreground">
          Connecting to the examination…
        </p>
      </Screen>
    );
  }

  if (state.phase === "waiting") {
    return (
      <Screen>
        <h1 className="hall-heading text-2xl font-bold">{state.examName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {state.refereeName}
        </p>
        <div className="mt-10 animate-pulse">
          <p className="hall-heading text-3xl font-bold text-accent">
            Waiting for start
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            The examination begins when the instructor starts it. Do not leave
            this screen.
          </p>
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          {state.totalQuestions} questions · {state.timePerQuestion}s each
        </p>
      </Screen>
    );
  }

  if (state.phase === "submitted") {
    return (
      <Screen>
        <h1 className="hall-heading text-2xl font-bold">
          {state.terminated ? "Examination ended" : "Examination submitted"}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {state.terminated
            ? "Your attempt was closed by the instructor. Please contact the examination staff."
            : "Your answers have been recorded. Results are announced by the examination staff."}
        </p>
      </Screen>
    );
  }

  const seconds = Math.ceil(remaining / 1000);
  const progress = Math.max(
    0,
    Math.min(1, remaining / (state.timePerQuestion * 1000)),
  );

  return (
    <div
      className={`unselectable relative flex min-h-screen flex-col transition-colors ${
        isDark ? "dark bg-slate-950 text-slate-100" : "bg-white text-slate-900"
      }`}
    >
      <header
        className={`flex items-center justify-between border-b px-6 py-4 text-sm shadow-sm transition-colors ${
          isDark
            ? "border-slate-800 bg-slate-900 text-slate-100"
            : "border-slate-200 bg-white text-slate-900"
        }`}
      >
        <span className="hall-heading font-bold">{state.examName}</span>
        <div className="flex items-center gap-4">
          <span
            className={`font-medium ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            Question {state.questionNumber} / {state.totalQuestions}
          </span>
          <button
            type="button"
            onClick={toggleDark}
            aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all cursor-pointer ${
              isDark
                ? "border-slate-700 bg-slate-800 text-amber-300 hover:bg-slate-700 hover:text-amber-200 shadow-sm"
                : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 shadow-sm"
            }`}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </header>
      <div className={`h-1.5 w-full ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
        <div
          className={`h-full transition-[width] duration-200 ease-linear ${
            isDark ? "bg-blue-500" : "bg-blue-600"
          }`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <main
        className={`flex flex-1 flex-col items-center justify-center px-6 py-10 text-center transition-colors ${
          isDark ? "bg-slate-950" : "bg-white"
        }`}
      >
        <p
          className={`hall-heading text-6xl font-black tabular-nums ${
            isDark ? "text-blue-400" : "text-blue-600"
          }`}
        >
          {seconds}
        </p>
        <p
          className={`text-xs uppercase tracking-widest font-bold mt-1 ${
            isDark ? "text-slate-500" : "text-slate-400"
          }`}
        >
          seconds remaining
        </p>

        <h1
          className={`mt-8 max-w-3xl text-2xl font-bold leading-relaxed sm:text-3xl ${
            isDark ? "text-white" : "text-slate-900"
          }`}
        >
          {state.questionText}
        </h1>

        <div className="mt-10 grid w-full max-w-2xl grid-cols-2 gap-5">
          {[true, false].map((value) => {
            const isSelected = state.selectedAnswer === value;

            const buttonClass = isSelected
              ? isDark
                ? "bg-blue-600 border-blue-500 text-white shadow-lg ring-4 ring-blue-500/30 scale-[1.02]"
                : "bg-blue-600 border-blue-700 text-white shadow-lg ring-4 ring-blue-600/25 scale-[1.02]"
              : isDark
                ? "bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700 shadow-sm"
                : "bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200 hover:border-slate-400 shadow-sm";

            return (
              <button
                key={String(value)}
                onClick={() => answer(value)}
                className={`hall-heading relative flex items-center justify-center gap-3 rounded-2xl border-2 py-8 text-2xl font-black transition-all cursor-pointer ${buttonClass}`}
              >
                <span>{value ? "TRUE" : "FALSE"}</span>
                {isSelected && (
                  <span className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/25 text-white text-xs font-black shadow-sm">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p
          className={`mt-8 text-xs font-medium ${
            isDark ? "text-slate-500" : "text-slate-400"
          }`}
        >
          {state.refereeName} · You cannot return to previous questions.
        </p>
      </main>
    </div>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-12 bg-slate-50 text-slate-900 transition-colors">
      <div className="w-full max-w-lg rounded-3xl p-8 text-center shadow-xl border border-slate-200/80 bg-white text-slate-900 transition-colors">
        {children}
      </div>
    </div>
  );
}
